import { DateOnly } from '@if/core/date'
import { formatCurrency, formatIsoDate } from '@if/core/utils'
import {
  ActionMenuItem,
  Banner,
  ControlledDebouncedNumberField,
  ControlledSelect,
  ErrorAlert,
  FormMethods,
  getErrorMessage,
  HighlightCallout,
  useFormDialog,
  useIfUser,
} from '@if/mui'
import { Stack } from '@mui/material'
import { useCalculateNumberOfPaymentsToClearOverdueAmount } from '@src/api/loan/loan-api'
import { getToday } from '@src/services/DateTimeProvider'
import { EOperationStatus, EPaymentMethod } from '@src/types/constants'
import { CustomerPaymentOperation } from '@src/types/loan/loan-operation'
import { SignedCalendarSimulation } from '@src/types/loan/loan-simulation'
import { Clock, TrendingUp } from 'lucide-react'
import { useEffect, useMemo } from 'react'
import { useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useGetCurrentOverdueAmount } from '../../LoanCalendar-hooks'
import { EditPaymentSchemaType } from '../EditPayments-schema'
import ReturnToCalendarAction, { RETURN_TO_CALENDAR, ToolResult } from '../EditPayments-tool'
import { BatchUpdatePaymentsSchemaType, batchUpdatePaymentsSchema } from './LoanBatchUpdatePayments-schema'

/** What the opener asked for: catching the overdue up, or moving every amount by a delta. */
export interface BatchUpdatePaymentsProps {
  isCatchUpMode: boolean
  isIncreasingPayments: boolean
}

/**
 * Catch-up rebuilds the tail of the calendar from the backend's answer; an adjustment only
 * shifts the amounts already in scope. The two land through different operations in the
 * editor, so the tool says which one it ran rather than leaving it to be re-derived.
 */
export type BatchUpdatePaymentsResult =
  | {
      kind: 'catchUp'
      fromDate: DateOnly
      numberOfPayments: number
      amount: number
      lastPaymentAmount: number
    }
  | { kind: 'adjust'; amount: number; fromPaymentDate: DateOnly | null }

interface LoanBatchUpdatePaymentsBodyProps {
  form: FormMethods<BatchUpdatePaymentsSchemaType>
  simulation: SignedCalendarSimulation
  payments: EditPaymentSchemaType[]
  isIncreasingPayments: boolean
}

const LoanBatchUpdatePaymentsBody = ({
  form,
  simulation,
  payments,
  isIncreasingPayments,
}: LoanBatchUpdatePaymentsBodyProps) => {
  const { t } = useTranslation()
  const { control, setValue } = form
  const user = useIfUser()
  const canBypassAcpLock = user.rights.canEditLockedPpaPayments

  const isCatchUpMode = useWatch({ control, name: 'isCatchUpMode' })
  const fromPaymentDate = useWatch({ control, name: 'fromPaymentDate' })
  const amount = useWatch({ control, name: 'amount' })

  const currentOverdueAmount = useGetCurrentOverdueAmount(simulation.simulation)

  const {
    data: paymentCalculationResult,
    isFetching: isCalculatingPayments,
    error: errorApi,
  } = useCalculateNumberOfPaymentsToClearOverdueAmount(
    simulation.simulation.fullLoan.loan.id,
    simulation.simulation.fullLoan.loan.financingCompanyId,
    simulation,
    fromPaymentDate ?? getToday(),
    amount ?? 0,
    isCatchUpMode && !!fromPaymentDate && !!amount && amount > 0,
  )

  // The answer the agent is looking at is the one Confirm applies, so it lives on the form and
  // gates the submit through the schema rather than being read back out of the query on submit.
  useEffect(() => {
    setValue('catchUpCalculation', isCatchUpMode ? (paymentCalculationResult ?? null) : null, { shouldValidate: true })
  }, [isCatchUpMode, paymentCalculationResult, setValue])

  const paymentDateOptions = useMemo(() => {
    if (!isCatchUpMode) {
      return payments
        .filter((p) => !p.isDeleted && !p.isLocked)
        .map((payment) => (
          <ActionMenuItem key={payment.id} value={formatIsoDate(payment.effectiveDate)}>
            {`${formatIsoDate(payment.effectiveDate)} - ${formatCurrency(payment.amount)}`}
          </ActionMenuItem>
        ))
    }

    const nextValidAction = simulation.simulation.getNextValidActionDate(
      EPaymentMethod.PreAuthorizedDebit,
      canBypassAcpLock,
    )
    return simulation.simulation.operationInformations
      .filter(
        (opInfo) =>
          opInfo.isCustomerPaymentOperation() &&
          opInfo.operation.operationStatusId === EOperationStatus.Planned &&
          opInfo.operation.effectiveDate.getTime() >= nextValidAction.getTime(),
      )
      .map((opInfo) => opInfo.operation as CustomerPaymentOperation)
      .sort((a, b) => a.effectiveDate.getTime() - b.effectiveDate.getTime())
      .map((payment) => (
        <ActionMenuItem key={payment.id} value={formatIsoDate(payment.effectiveDate)}>
          {`${formatIsoDate(payment.effectiveDate)} - ${formatCurrency(payment.amount)}`}
        </ActionMenuItem>
      ))
  }, [isCatchUpMode, simulation, payments, canBypassAcpLock])

  const operationLabel =
    isCatchUpMode || isIncreasingPayments
      ? t('loan.payment.batchUpdatePayments.increase')
      : t('loan.payment.batchUpdatePayments.decrease')

  const error = useMemo(() => {
    if (!isCalculatingPayments && isCatchUpMode && currentOverdueAmount <= 0)
      return t('loan.payment.batchUpdatePayments.errors.noOverdueAmount')

    // TODO: Replace with translated business error from API
    const formattedError = getErrorMessage(errorApi)
    if (formattedError?.toLowerCase().includes('unable to clear overdue amount')) {
      return t('loan.payment.batchUpdatePayments.errors.unableToClearOverdue')
    }

    return errorApi
  }, [isCalculatingPayments, isCatchUpMode, currentOverdueAmount, t, errorApi])

  return (
    <Stack spacing={2}>
      {error && <ErrorAlert error={error} />}
      {isCatchUpMode && (
        <>
          <HighlightCallout
            icon={Clock}
            subject={t('loan.payment.batchUpdatePayments.overdueAmount')}
            value={formatCurrency(currentOverdueAmount)}
          />
          <ControlledSelect
            control={control}
            name="fromPaymentDate"
            options={paymentDateOptions}
            parseValue={(v: string | null) => (v ? new DateOnly(v) : null)}
            label={t('loan.payment.batchUpdatePayments.fromPaymentDate')}
            disableEmptyOption
          />
        </>
      )}
      <ControlledDebouncedNumberField
        control={control}
        name="amount"
        isMoney
        label={t('loan.payment.batchUpdatePayments.amountLabel', { op: operationLabel })}
        autoFocus={!isCatchUpMode}
        debounceTime={350}
      />
      {isCatchUpMode && !isCalculatingPayments && paymentCalculationResult && (
        <Banner severity="info">
          {t('loan.payment.batchUpdatePayments.catchUpInfo', {
            count: paymentCalculationResult.numberOfPayments,
            amount: formatCurrency(paymentCalculationResult.lastPaymentAmount),
          })}
        </Banner>
      )}
    </Stack>
  )
}

interface UseLoanBatchUpdatePaymentsDialogProps {
  simulation: SignedCalendarSimulation
  payments: EditPaymentSchemaType[]
}

/** Moves every amount in scope by one delta, or catches the overdue up from a chosen payment. */
export const useLoanBatchUpdatePaymentsDialog = ({ simulation, payments }: UseLoanBatchUpdatePaymentsDialogProps) => {
  const { t } = useTranslation()

  return useFormDialog<BatchUpdatePaymentsSchemaType, BatchUpdatePaymentsProps, ToolResult<BatchUpdatePaymentsResult>>({
    icon: TrendingUp,
    title: t('loan.payment.batchUpdatePayments.title'),
    confirmLabel: t('common.confirm'),
    maxWidth: 'sm',
    fullWidth: true,
    schema: batchUpdatePaymentsSchema,
    defaultValues: (props) => ({ isCatchUpMode: props.isCatchUpMode }),
    successMessage: false,
    additionalActions: (ctx) => <ReturnToCalendarAction onReturn={() => ctx.close(RETURN_TO_CALENDAR)} />,
    content: ({ form, props }) => (
      <LoanBatchUpdatePaymentsBody
        form={form}
        simulation={simulation}
        payments={payments}
        isIncreasingPayments={props.isIncreasingPayments}
      />
    ),
    onSubmit: (data, { props }) => {
      // The schema only lets both of these through together, and only in catch-up mode.
      if (data.isCatchUpMode && data.fromPaymentDate && data.catchUpCalculation) {
        return {
          kind: 'catchUp',
          fromDate: data.fromPaymentDate,
          numberOfPayments: data.catchUpCalculation.numberOfPayments,
          amount: data.amount,
          lastPaymentAmount: data.catchUpCalculation.lastPaymentAmount,
        }
      }
      return {
        kind: 'adjust',
        amount: props.isIncreasingPayments ? data.amount : -data.amount,
        fromPaymentDate: data.fromPaymentDate,
      }
    },
  })
}
